import { apiFetch } from "@/hooks/apiFetch"
import React, { useEffect, useState } from "react"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material"
import ClearIcon from "@mui/icons-material/Clear"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import { useCrudContext } from "@/context/crudContext"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

const CashDenominationForm = ({ type, onSubmit, onClose, editData }) => {
  const [nprNotes, setNprNotes] = useState([])
  const [currencies, setCurrencies] = useState([])
  const [denominations, setDenominations] = useState([])
  const [otherCurrencyDenominations, setOtherCurrencyDenominations] = useState(
    []
  )
  const [showOtherCurrency, setShowOtherCurrency] = useState(false)
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch NPR denominations and currencies from API
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true)
        const res = await apiFetch("cash/currencies-notes")
        const notes = res.denominations.npr

        setNprNotes(notes)
        setCurrencies(res.currencies || [])

        if (editData?.denominations?.length) {
          const stageFiltered = editData.denominations.filter(
            (d) => d.stage === type
          )

          const npr = stageFiltered
            .filter((d) => d.currency_code === "npr")
            .map((d) => ({
              denomination: Number(d.denomination),
              quantity: Number(d.quantity),
              currency_code: "npr",
              exchange_rate_to_npr: Number(d.exchange_rate_to_npr),
            }))

          const other = stageFiltered
            .filter((d) => d.currency_code !== "npr")
            .map((d) => ({
              denomination: Number(d.denomination),
              quantity: Number(d.quantity),
              currency_code: d.currency_code,
              exchange_rate_to_npr: Number(d.exchange_rate_to_npr),
            }))

          setDenominations(npr)
          setOtherCurrencyDenominations(other)
          return
        }

        // 🟢 CREATE MODE
        setDenominations(
          notes.map((note) => ({
            denomination: note,
            quantity: 0,
            currency_code: "npr",
            exchange_rate_to_npr: 1,
          }))
        )
      } catch (err) {
        toast.error("Failed to fetch currency notes")
      } finally {
        setIsLoading(false)
      }
    }

    init()
  }, [editData, type])

  // Recalculate total whenever quantities change
  useEffect(() => {
    const nprTotal = denominations.reduce(
      (sum, item) =>
        sum +
        item.denomination * item.quantity * (item.exchange_rate_to_npr || 1),
      0
    )

    const otherCurrencyTotal = otherCurrencyDenominations.reduce(
      (sum, item) =>
        sum +
        item.denomination * item.quantity * (item.exchange_rate_to_npr || 1),
      0
    )

    setTotal(nprTotal + otherCurrencyTotal)
  }, [denominations, otherCurrencyDenominations])

  const handleQuantityChange = (index, value) => {
    const newDenominations = [...denominations]
    newDenominations[index].quantity = value
    setDenominations(newDenominations)
  }

  const handleOtherCurrencyAdd = () => {
    const otherCurrencies = currencies.filter((c) => c.toLowerCase() !== "npr")
    if (otherCurrencies.length === 0) {
      toast.error("No other currencies available")
      return
    }

    setOtherCurrencyDenominations([
      ...otherCurrencyDenominations,
      {
        currency_code: otherCurrencies[0].toLowerCase(),
        denomination: "",
        quantity: 0,
        exchange_rate_to_npr: 1,
      },
    ])
  }

  const handleOtherCurrencyRemove = (index) => {
    const newOther = [...otherCurrencyDenominations]
    newOther.splice(index, 1)
    setOtherCurrencyDenominations(newOther)
  }

  const handleOtherCurrencyChange = (index, field, value) => {
    const newOther = [...otherCurrencyDenominations]
    newOther[index][field] =
      field === "quantity" ||
      field === "denomination" ||
      field === "exchange_rate_to_npr"
        ? field === "quantity"
          ? parseInt(value) || 0
          : parseFloat(value) || 0
        : value
    setOtherCurrencyDenominations(newOther)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Combine NPR denominations and other currency denominations
    const allDenominations = [
      ...denominations.map((item) => ({
        denomination: item.denomination,
        quantity: item.quantity,
        currency_code: item.currency_code || "npr",
        exchange_rate_to_npr: item.exchange_rate_to_npr || 1,
      })),
      ...otherCurrencyDenominations
        .filter((item) => item.denomination && item.quantity > 0)
        .map((item) => ({
          denomination: item.denomination,
          quantity: item.quantity,
          currency_code: item.currency_code.toLowerCase(),
          exchange_rate_to_npr: item.exchange_rate_to_npr || 1,
        })),
    ]
    onSubmit(allDenominations)
  }

  const otherCurrencies = currencies.filter((c) => c.toLowerCase() !== "npr")

  return (
    <form onSubmit={handleSubmit} className="p-4">
      {isLoading ? (
        <div className="text-center py-8">Loading denominations...</div>
      ) : (
        <>
          {/* NPR Denominations */}
          <Typography variant="subtitle2" className="mb-3 font-semibold">
            NPR Denominations
          </Typography>
          {denominations.map((item, index) => (
            <div key={item.denomination} className="flex items-center mb-4">
              <span className="w-24 font-medium">{item.denomination} NPR</span>
              <TextField
                type="number"
                inputProps={{ min: 0 }}
                value={item.quantity}
                onFocus={(event) => {
                  event.target.select()
                }}
                onChange={(e) =>
                  handleQuantityChange(index, parseInt(e.target.value) || 0)
                }
                className="flex-1"
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "white",
                  },
                }}
              />
            </div>
          ))}

          {/* Other Currency Section */}
          {otherCurrencies.length > 0 && (
            <>
              <Divider className="my-4" />
              <div className="flex items-center justify-between mb-3">
                <Typography variant="subtitle2" className="font-semibold">
                  Other Currencies
                </Typography>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOtherCurrencyAdd}
                  className="capitalize"
                >
                  Add Currency
                </Button>
              </div>

              {otherCurrencyDenominations.map((item, index) => (
                <Box
                  key={index}
                  className="mb-4 p-3 border border-gray-200 rounded"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Typography variant="caption" className="text-gray-600">
                      Entry #{index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleOtherCurrencyRemove(index)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                  <div className="space-y-3">
                    <FormControl fullWidth size="small">
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={item.currency_code || ""}
                        label="Currency"
                        onChange={(e) =>
                          handleOtherCurrencyChange(
                            index,
                            "currency_code",
                            e.target.value
                          )
                        }
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "white",
                          },
                        }}
                      >
                        {otherCurrencies.map((currency) => (
                          <MenuItem
                            key={currency}
                            value={currency.toLowerCase()}
                          >
                            {currency}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Denomination"
                      type="number"
                      inputProps={{ min: 0, step: "any" }}
                      value={item.denomination}
                      onFocus={(event) => {
                        event.target.select()
                      }}
                      onChange={(e) =>
                        handleOtherCurrencyChange(
                          index,
                          "denomination",
                          e.target.value
                        )
                      }
                      size="small"
                      sx={{
                        "& .MuiInputBase-input": {
                          backgroundColor: "white",
                        },
                      }}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <TextField
                        label="Quantity"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={item.quantity}
                        onFocus={(event) => {
                          event.target.select()
                        }}
                        onChange={(e) =>
                          handleOtherCurrencyChange(
                            index,
                            "quantity",
                            e.target.value
                          )
                        }
                        size="small"
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                      <TextField
                        label="Exchange Rate (to NPR)"
                        type="number"
                        inputProps={{ min: 0.01, step: "any" }}
                        value={item.exchange_rate_to_npr}
                        onFocus={(event) => {
                          event.target.select()
                        }}
                        onChange={(e) =>
                          handleOtherCurrencyChange(
                            index,
                            "exchange_rate_to_npr",
                            e.target.value
                          )
                        }
                        size="small"
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </div>
                  </div>
                </Box>
              ))}
            </>
          )}

          <div className="flex justify-between items-center mt-6 p-3 bg-gray-50 rounded">
            <span className="font-bold text-lg">Total NPR:</span>
            <span className="font-bold text-lg">{total.toLocaleString()}</span>
          </div>

          <div className="flex justify-end items-center gap-2 mt-6">
            <Button
              onClick={onClose}
              variant="outlined"
              color="error"
              className="capitalize rounded-md font-medium text-[13px] py-[11px] px-[30px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="capitalize rounded-md font-medium text-[13px] py-[11px] px-[30px] text-white"
            >
              {type === "opening" ? "Open Cash" : "Close Cash"}
            </Button>
          </div>
        </>
      )}
    </form>
  )
}

const AddCashDenominationModal = ({ editData = null, onClose = () => {} }) => {
  const { openDialog, setOpenDialog } = useCrudContext()
  const getInitialType = () => {
    if (!editData) return "opening"
    console.log("edit test", editData.status)
    return editData.status === "closed" ? "closing" : "opening"
  }
  const [type, setType] = useState(getInitialType)
  const queryClient = useQueryClient()

  const defaultOnClose = () => {
    setOpenDialog(false)
    setType("opening") // Reset to default type
    onClose ? onClose() : null
  }

  const handleSubmit = async (denominations) => {
    try {
      if (editData?.id) {
        // Update existing cash session
        const response = await apiFetch(`cash/${editData.id}`, {
          method: "POST", // your backend uses POST for update
          body: JSON.stringify({ denominations }),
        })

        if (response) {
          queryClient.invalidateQueries({ queryKey: ["cash"] })
          toast.success("Cash session updated successfully")
          defaultOnClose()
        }
      } else if (type === "opening") {
        // Open cash session
        const response = await apiFetch("cash/open", {
          method: "POST",
          body: JSON.stringify({ denominations }),
        })

        if (response) {
          queryClient.invalidateQueries({ queryKey: ["cash"] })
          toast.success("Opening cash submitted successfully")
          defaultOnClose()
        }
      } else {
        // Close cash session - need to get current session first
        const currentSessionRes = await apiFetch("cash/current-session")

        if (!currentSessionRes?.data) {
          toast.error(
            "No open cash session found. Please open a session first."
          )
          return
        }

        const sessionId = currentSessionRes.data.id
        const response = await apiFetch(`cash/close/${sessionId}`, {
          method: "POST",
          body: JSON.stringify({
            denominations,
            cash_sales: 0, // Default to 0, can be updated later if needed
          }),
        })

        if (response) {
          queryClient.invalidateQueries({ queryKey: ["cash"] })
          toast.success("Closing cash submitted successfully")
          defaultOnClose()
        }
      }
    } catch (err) {
      console.error(`Failed to submit ${type} cash`, err)
      toast.error(`Failed to submit ${type} cash`)
    }
  }

  return (
    <Modal open={openDialog} onClose={defaultOnClose}>
      <Box
        sx={{
          width: { xs: "95vw", sm: "90vw", md: "500px" },
          margin: "30px auto",
          bgcolor: "white",
          borderRadius: 1,
          boxShadow: 3,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: 1,
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
            }}
            className="text-black capitalize"
          >
            {editData ? `Edit ${type} Cash` : `${type} Cash Denomination`}
          </Typography>
          <IconButton aria-label="remove" size="small" onClick={defaultOnClose}>
            <ClearIcon />
          </IconButton>
        </Box>

        {/* Type Selection */}
        {!editData && (
          <Box sx={{ padding: 2, borderBottom: "1px solid #e0e0e0" }}>
            <div className="flex gap-2">
              <Button
                variant={type === "opening" ? "contained" : "outlined"}
                onClick={() => setType("opening")}
                size="small"
              >
                Opening
              </Button>
              <Button
                variant={type === "closing" ? "contained" : "outlined"}
                onClick={() => setType("closing")}
                size="small"
              >
                Closing
              </Button>
            </div>
          </Box>
        )}

        {/* Form Body */}
        <CashDenominationForm
          type={type}
          onSubmit={handleSubmit}
          onClose={defaultOnClose}
          editData={editData}
        />
      </Box>
    </Modal>
  )
}

export default AddCashDenominationModal
